/**
 * User và Role OpenCore (`RpaOpenCoreUsersView`, `OpenCoreUserFormDialog`, `OpenCoreRoleFormDialog` của native): lọc,
 * mốc "x giây trước", kiểm biểu mẫu, khi nào phải hỏi lại trước khi thêm vào role `admins`, và thân gửi máy chủ.
 * Ràng buộc chép từ `modules/rpa/backend/src/opencore/quan-tri.service.ts` (`docUser`, `docRole`).
 */
import type { RoleOpenCore, ThanRoleOpenCore, ThanUserOpenCore, UserOpenCore } from '../types.ts';

export const MAT_KHAU_TOI_THIEU = 8;
export const MAT_KHAU_TOI_DA = 128;
export const GIOI_HAN_USER = { ten: 200, username: 128, email: 254 } as const;
export const GIOI_HAN_ROLE = { ten: 200, moTa: 1000 } as const;
const USERNAME = /^[a-z0-9._@+-]{3,128}$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const LOC_TRANG_THAI_USER = ['enabled', 'disabled', 'online', 'rpa', 'unvalidated'] as const;
/** Giá trị lọc "chưa có role" (không trùng được id OpenCore 24 hex). */
export const KHONG_ROLE = '__none__';

export interface BoLocUser {
  tim: string;
  status: string;
  /** `yes` | `no` | rỗng. */
  validated: string;
  role: string;
}

function hopTrangThai(u: UserOpenCore, loc: string): boolean {
  switch (loc) {
    case 'enabled':
      return !u.disabled;
    case 'disabled':
      return u.disabled;
    case 'online':
      return u.online;
    case 'rpa':
      return Boolean(u.rpaHeartbeat);
    case 'unvalidated':
      return !u.validated;
    default:
      return true;
  }
}

function hopRole(u: UserOpenCore, roleId: string): boolean {
  if (!roleId) return true;
  const ids = u.roles.map((r) => r.id);
  // Không đọc được role thì không khẳng định được "chưa có role".
  return roleId === KHONG_ROLE ? u.rolesStatus !== 'unavailable' && ids.length === 0 : ids.includes(roleId);
}

export function locUser(ds: readonly UserOpenCore[], loc: BoLocUser): UserOpenCore[] {
  const q = loc.tim.trim().toLowerCase();
  return ds.filter(
    (u) =>
      (!q || [u.id, u.name, u.username, u.email, ...u.roles.map((r) => r.name)].join(' ').toLowerCase().includes(q)) &&
      (!loc.validated || u.validated === (loc.validated === 'yes')) &&
      hopTrangThai(u, loc.status) &&
      hopRole(u, loc.role),
  );
}

/** Lựa chọn lọc role: role đọc được từ máy chủ, cộng role đã gắn trên User (khi danh sách role chưa tải được). */
export function luaChonRole(roles: readonly RoleOpenCore[] | undefined, users: readonly UserOpenCore[]): { id: string; name: string }[] {
  const m = new Map<string, string>();
  for (const r of roles ?? []) m.set(r.id, r.name);
  for (const u of users) for (const r of u.roles) if (r.id && !m.has(r.id)) m.set(r.id, r.name || r.id);
  return [...m].map(([id, name]) => ({ id, name }));
}

export function locRole(ds: readonly RoleOpenCore[], tim: string): RoleOpenCore[] {
  const q = tim.trim().toLowerCase();
  return q ? ds.filter((r) => [r.id, r.name, r.description].join(' ').toLowerCase().includes(q)) : [...ds];
}

/** Dưới một giờ: số giây/phút trước; quá một giờ, tương lai hoặc không đọc được: `null` (phía gọi hiện mốc đầy đủ). */
export function thoiGianTruoc(iso: string | null, now = Date.now()): { giay: number } | { phut: number } | null {
  if (!iso) return null;
  const tuoi = (now - Date.parse(iso)) / 1000;
  if (!Number.isFinite(tuoi) || tuoi < 0 || tuoi >= 3600) return null;
  return tuoi < 60 ? { giay: Math.floor(tuoi) } : { phut: Math.floor(tuoi / 60) };
}

// ─────────────── Biểu mẫu User ───────────────

export interface FormUser {
  name: string;
  username: string;
  email: string;
  password: string;
  confirm: string;
  enabled: boolean;
  validated: boolean;
  emailValidated: boolean;
  roleIds: string[];
}

export function formTuUser(u: UserOpenCore | null): FormUser {
  return {
    name: u?.name ?? '',
    username: u?.username ?? '',
    email: u?.email ?? '',
    password: '',
    confirm: '',
    enabled: u ? !u.disabled : true,
    validated: u?.validated ?? true,
    emailValidated: u?.emailValidated ?? false,
    roleIds: (u?.roles ?? []).map((r) => r.id).filter(Boolean),
  };
}

export type TruongUser = 'name' | 'username' | 'email' | 'password' | 'confirm';
export type LoiUser = Partial<Record<TruongUser, 'required' | 'invalid' | 'short' | 'long' | 'mismatch'>>;

export function kiemUser(v: FormUser, taoMoi: boolean): LoiUser {
  const loi: LoiUser = {};
  if (!v.name.trim()) loi.name = 'required';
  const username = v.username.trim();
  if (!username) loi.username = 'required';
  else if (!USERNAME.test(username)) loi.username = 'invalid';
  if (v.email.trim() && !EMAIL.test(v.email.trim())) loi.email = 'invalid';
  if (taoMoi) {
    if (v.password.length < MAT_KHAU_TOI_THIEU) loi.password = 'short';
    else if (v.password.length > MAT_KHAU_TOI_DA) loi.password = 'long';
    if (v.password !== v.confirm) loi.confirm = 'mismatch';
  }
  return loi;
}

export const cungTapId = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** Có role `admins` mới được thêm (so với lúc mở hộp) thì phải hỏi lại; máy chủ cũng chặn `409` nếu thiếu xác nhận. */
export function canXacNhanAdmin(moi: readonly string[], cu: readonly string[], roles: readonly RoleOpenCore[] | undefined): boolean {
  const admins = (roles ?? []).filter((r) => r.isAdmins).map((r) => r.id);
  return moi.some((id) => admins.includes(id) && !cu.includes(id));
}

/**
 * Thân tạo/sửa User. Tạo không gán role (gán sau bằng Sửa, như native). Sửa chỉ gửi `roleIds` khi đổi và khi biết role
 * hiện tại của User — gửi khi không biết sẽ gỡ nhầm.
 */
export function thanUser(v: FormUser, o: { taoMoi: boolean; roleCu: readonly string[]; bietRole: boolean; xacNhanAdmin: boolean }): ThanUserOpenCore {
  return {
    name: v.name.trim(),
    username: v.username.trim().toLowerCase(),
    email: v.email.trim(),
    enabled: v.enabled,
    validated: v.validated,
    emailValidated: v.emailValidated,
    ...(o.taoMoi ? { password: v.password } : {}),
    ...(!o.taoMoi && o.bietRole && !cungTapId(v.roleIds, o.roleCu) ? { roleIds: v.roleIds } : {}),
    confirmAdmin: o.xacNhanAdmin,
  };
}

// ─────────────── Biểu mẫu Role ───────────────

/** Thành viên mới được thêm so với lúc mở hộp. */
export const thanhVienThem = (moi: readonly string[], cu: readonly string[]) => moi.filter((id) => !cu.includes(id));

/**
 * Thân tạo/sửa role. Sửa chỉ gửi `memberIds` khi đổi và role không do License quản (máy chủ chặn `409`). Role hệ thống
 * không đổi tên: giữ tên cũ.
 */
export function thanRole(
  v: { name: string; description: string; memberIds: string[] },
  goc: RoleOpenCore | null,
  xacNhanAdmin: boolean,
): ThanRoleOpenCore {
  const cu = goc?.members.map((m) => m.id) ?? [];
  const doi = !cungTapId(v.memberIds, cu);
  return {
    name: goc?.system ? goc.name : v.name.trim(),
    description: v.description.trim(),
    ...(!goc || (doi && goc.managedBy !== 'license') ? { memberIds: v.memberIds } : {}),
    confirmAdmin: xacNhanAdmin,
  };
}
